import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Header } from "../components/Header";
import { ChevronLeft, Plus, Trash2, AlertTriangle, Loader2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `/api`;
const SYMPTOMS = ['Palpitations', 'Sueurs', 'Tremblements', 'Nausees', 'Vertiges', 'Oppression thoracique', 'Souffle court', 'Gorge serree', 'Maux de tete', 'Tensions musculaires', 'Fatigue soudaine', 'Picotements'];

export default function CrisisJournalPage() {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: '', trigger: '', physical_symptoms: [],
    intensity: 5, thoughts: '', actions_taken: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchEntries = () => axios.get(`${API}/crisis`).then(r => setEntries(r.data));
  useEffect(() => { fetchEntries(); }, []);

  const toggleSymptom = (s) => {
    setForm(prev => ({
      ...prev,
      physical_symptoms: prev.physical_symptoms.includes(s)
        ? prev.physical_symptoms.filter(x => x !== s)
        : [...prev.physical_symptoms, s]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/crisis`, form);
      setShowForm(false);
      setForm({ date: new Date().toISOString().split('T')[0], time: new Date().toTimeString().slice(0, 5), location: '', trigger: '', physical_symptoms: [], intensity: 5, thoughts: '', actions_taken: '', notes: '' });
      fetchEntries();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/crisis/${id}`);
    fetchEntries();
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/dashboard" className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200"><ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour</Link>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Carnet de crise</h1>
            <p className="text-[#6A7771] text-sm mt-1">Notez vos crises pour mieux les comprendre</p>
          </div>
          <button onClick={() => setShowForm(!showForm)} className="px-5 py-2.5 bg-[#D9A892] text-white rounded-full text-sm font-medium hover:bg-[#C2937E] transition-all duration-300 flex items-center gap-1.5" data-testid="new-crisis-btn">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Nouvelle entrée
          </button>
        </motion.div>

        <AnimatePresence>
          {showForm && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl border border-[#EBE7E0] p-6 mb-8 space-y-5 overflow-hidden"
              data-testid="crisis-form"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Date</label>
                  <input type="date" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="crisis-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Heure</label>
                  <input type="time" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300" value={form.time} onChange={e => setForm({...form, time: e.target.value})} data-testid="crisis-time" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Lieu</label>
                <input type="text" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300" value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Maison, travail, transport..." data-testid="crisis-location" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Déclencheur / Stimuli</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] min-h-[70px] transition-all duration-300" value={form.trigger} onChange={e => setForm({...form, trigger: e.target.value})} placeholder="Qu'est-ce qui a déclenché la crise ?" data-testid="crisis-trigger" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-2.5">Symptômes physiques</label>
                <div className="flex flex-wrap gap-2">
                  {SYMPTOMS.map(s => (
                    <button key={s} type="button" onClick={() => toggleSymptom(s)}
                      className={`px-3.5 py-1.5 rounded-full text-xs border transition-all duration-300 ${form.physical_symptoms.includes(s) ? 'bg-[#D9A892] text-white border-[#D9A892]' : 'bg-[#FAF9F6] text-[#6A7771] border-[#EBE7E0] hover:border-[#D9A892]/40'}`}
                      data-testid={`symptom-${s}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Intensité ({form.intensity}/10)</label>
                <input type="range" min="1" max="10" value={form.intensity} onChange={e => setForm({...form, intensity: parseInt(e.target.value)})} className="w-full" data-testid="crisis-intensity" />
                <div className="flex justify-between text-xs text-[#6A7771] mt-1"><span>Faible</span><span>Forte</span></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Pensées du moment</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] min-h-[70px] transition-all duration-300" value={form.thoughts} onChange={e => setForm({...form, thoughts: e.target.value})} placeholder="Qu'est-ce qui traversait votre esprit ?" data-testid="crisis-thoughts" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Actions prises</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] min-h-[70px] transition-all duration-300" value={form.actions_taken} onChange={e => setForm({...form, actions_taken: e.target.value})} placeholder="Qu'avez-vous fait pour gérer la crise ?" data-testid="crisis-actions" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Notes complémentaires</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] min-h-[70px] transition-all duration-300" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="crisis-notes" />
              </div>
              <button type="submit" disabled={saving} className="w-full py-3 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-50 text-sm" data-testid="crisis-submit">
                {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Enregistrer'}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        <div className="space-y-3">
          {entries.map((entry, i) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-white rounded-3xl border border-[#EBE7E0] p-5 hover:shadow-[0_10px_40px_rgba(44,53,49,0.06)] transition-all duration-300"
              data-testid={`crisis-entry-${entry.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className="text-sm font-medium text-[#2C3531]">{entry.date} a {entry.time}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${entry.intensity >= 7 ? 'bg-red-50 text-[#B86666]' : entry.intensity >= 4 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-[#6B8576]'}`}>
                      {entry.intensity}/10
                    </span>
                  </div>
                  {entry.location && <p className="text-xs text-[#6A7771] mb-1">Lieu : {entry.location}</p>}
                  {entry.trigger && <p className="text-sm text-[#6A7771] mb-1.5"><span className="font-medium text-[#2C3531]">Déclencheur :</span> {entry.trigger}</p>}
                  {entry.physical_symptoms.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {entry.physical_symptoms.map(s => <span key={s} className="px-2.5 py-0.5 bg-[#D9A892]/10 text-[#C2937E] rounded-full text-xs">{s}</span>)}
                    </div>
                  )}
                </div>
                <button onClick={() => handleDelete(entry.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200 flex-shrink-0 ml-3"><Trash2 className="w-4 h-4 text-[#B86666]" strokeWidth={1.5} /></button>
              </div>
            </motion.div>
          ))}
          {entries.length === 0 && !showForm && (
            <div className="text-center py-16 text-[#6A7771]">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-20" strokeWidth={1.5} />
              <p className="font-medium">Aucune entrée pour le moment</p>
              <p className="text-sm mt-1 opacity-70">C'est une bonne chose</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
